import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/shared/components/PageHeader";
import { toast } from "@/shared/lib/toast";

import { useExportOrders } from "../api/use-export-orders";
import { ExportConfig, type ExportSelection } from "../components/ExportConfig";
import {
  downloadCsv,
  exportFileName,
  filterByDateRange,
  ordersToCsv,
} from "../lib/order-export-csv";

// ── Page ─────────────────────────────────────────────────────────────────────
export function OrdersExportPage(): JSX.Element {
  const { t } = useTranslation("orders");
  const mutation = useExportOrders();

  function handleGenerate(sel: ExportSelection): void {
    mutation.mutate(
      {
        // exactOptionalPropertyTypes: include a filter only when it has a value.
        ...(sel.marketplace && { marketplace: sel.marketplace }),
        ...(sel.status && { status: sel.status }),
        ...(sel.seller && { seller_id: sel.seller }),
      },
      {
        onSuccess: (orders) => {
          // Date range is filtered client-side: the orders list API has no date params.
          const rows = filterByDateRange(orders, sel.dateFrom, sel.dateTo);
          if (rows.length === 0) {
            toast.info(t("oe_empty"));
            return;
          }
          downloadCsv(exportFileName(sel.dateFrom, sel.dateTo), ordersToCsv(rows, sel.columns));
          toast.success(t("oe_done", { count: rows.length }));
        },
        onError: () => toast.error(t("oe_failed")),
      },
    );
  }

  return (
    <div>
      <PageHeader title={t("title_orders_export")} />

      <div className="max-w-2xl">
        <ExportConfig onGenerate={handleGenerate} isGenerating={mutation.isPending} />
      </div>
    </div>
  );
}
